import { useState, useEffect, useCallback } from 'react';
import { Client } from '../types/multiTenant';
import clientService from '../services/clientService';
import { useOrganization } from '../contexts/OrganizationContext';

/**
 * Hook for managing clients
 * Automatically loads clients for the current organization
 */
export const useClients = () => {
  const { organization, currentUser } = useOrganization();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load all clients
   */
  // Dependency em organization?.id (primitivo) em vez do objeto inteiro:
  // assim, mesmo se o Context entregar novo objeto a cada re-render, este
  // useCallback NÃO recria, e o useEffect abaixo não dispara fetch redundante.
  const loadClients = useCallback(async () => {
    if (!organization) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await clientService.listClients(organization.id);
      setClients(data);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  /**
   * Create new client
   * Validates plan limits before creating
   */
  const createClient = useCallback(
    async (data: Omit<Parameters<typeof clientService.createClient>[0], 'organizationId' | 'createdBy'>) => {
      if (!organization || !currentUser) {
        const errorMsg = 'Organization or user not loaded';
        setError(errorMsg);
        console.error('createClient failed:', { organization: !!organization, currentUser: !!currentUser });
        throw new Error(errorMsg);
      }

      setIsLoading(true);
      setError(null);

      try {
        // Validate plan limits before creating
        const maxClients = organization.maxClients ?? -1; // -1 means unlimited
        const validation = await clientService.canCreateClient(organization.id, maxClients);

        if (!validation.canCreate) {
          const errorMsg = validation.message || `Limite de ${maxClients} clientes atingido. Faça upgrade do plano.`;
          console.warn('Client creation blocked by plan limit:', validation);
          setError(errorMsg);
          throw new Error(errorMsg);
        }

        const newClient = await clientService.createClient({
          ...data,
          organizationId: organization.id,
          createdBy: currentUser.id,
        });

        if (!newClient) {
          const errorMsg = 'Failed to create client - service returned null';
          console.error(errorMsg, { organizationId: organization.id, createdBy: currentUser.id });
          setError(errorMsg);
          throw new Error(errorMsg);
        }

        setClients((prev) => [...prev, newClient]);
        return newClient;
      } catch (err) {
        console.error('Error creating client:', err);
        const errorMsg = err instanceof Error ? err.message : 'Failed to create client';
        setError(errorMsg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [organization, currentUser]
  );

  /**
   * Update client
   */
  const updateClient = useCallback(
    async (clientId: string, updates: Partial<Client>) => {
      setIsLoading(true);
      setError(null);

      try {
        const updated = await clientService.updateClient(clientId, updates);

        if (updated) {
          setClients((prev) =>
            prev.map((client) => (client.id === clientId ? updated : client))
          );
        }

        return updated;
      } catch (err) {
        console.error('Error updating client:', err);
        setError('Failed to update client');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Delete client (soft delete)
   */
  const deleteClient = useCallback(async (clientId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await clientService.deleteClient(clientId);

      if (success) {
        setClients((prev) => prev.filter((client) => client.id !== clientId));
      }

      return success;
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Search clients
   */
  const searchClients = useCallback(
    async (query: string) => {
      if (!organization) return [];

      setIsLoading(true);
      setError(null);

      try {
        const results = await clientService.searchClients(organization.id, query);
        return results;
      } catch (err) {
        console.error('Error searching clients:', err);
        setError('Failed to search clients');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [organization]
  );

  /**
   * Get clients by tag
   */
  const getClientsByTag = useCallback(
    async (tag: string) => {
      if (!organization) return [];

      setIsLoading(true);
      setError(null);

      try {
        const results = await clientService.getClientsByTag(organization.id, tag);
        return results;
      } catch (err) {
        console.error('Error getting clients by tag:', err);
        setError('Failed to get clients by tag');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [organization]
  );

  /**
   * Get client statistics
   */
  const getClientStats = useCallback(async () => {
    if (!organization) return null;

    try {
      const stats = await clientService.getClientStats(organization.id);
      return stats;
    } catch (err) {
      console.error('Error getting client stats:', err);
      return null;
    }
  }, [organization]);

  /**
   * Refresh clients
   */
  const refresh = useCallback(() => {
    loadClients();
  }, [loadClients]);

  /**
   * Check if organization can create more clients
   */
  const canCreateClient = useCallback(async () => {
    if (!organization) return { canCreate: false, message: 'Organization not loaded' };

    const maxClients = organization.maxClients ?? -1;
    return clientService.canCreateClient(organization.id, maxClients);
  }, [organization]);

  /**
   * Get current limit info (sync, uses local state)
   */
  const getLimitInfo = useCallback(() => {
    if (!organization) {
      return { currentCount: 0, limit: -1, isAtLimit: false, percentage: 0 };
    }

    const maxClients = organization.maxClients ?? -1;
    const currentCount = clients.length;
    const isAtLimit = maxClients !== -1 && currentCount >= maxClients;
    const percentage = maxClients === -1 ? 0 : Math.min((currentCount / maxClients) * 100, 100);

    return {
      currentCount,
      limit: maxClients,
      isAtLimit,
      percentage
    };
  }, [organization, clients.length]);

  // Auto-load on mount and when organization id changes.
  // Antes dependia de `organization` (objeto) + `loadClients`, ambos podem
  // mudar referência sem mudar conteúdo, causando re-fetch desnecessário.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (organization?.id) {
      loadClients();
    }
  }, [organization?.id]);

  return {
    // Data
    clients,
    isLoading,
    error,

    // Actions
    createClient,
    updateClient,
    deleteClient,
    searchClients,
    getClientsByTag,
    getClientStats,
    refresh,

    // Limit info
    canCreateClient,
    getLimitInfo,
  };
};

export default useClients;
